import { CreateLoaderTable } from "@/components/Loaders";
import { Main } from "@/components/Main";
import { useEffect, useState } from "react";
import { ApiDataProps } from "../Tenants";
import { TableData } from "@/App/InvestigationSales/Table";
import { getInvestigationSales, InvestigationSalesGrouped } from "@/Api/sales";
import { usePermissions } from "@/hooks/usePermissions";
import { If } from "@/components/If";
import { SelectComponent } from "@/components/SelectComponent";

const CONTEST_STATUS = {
  CREATED: 1,
  EXPIRED: 2,
  FINISHED: 3,
  CANCELED: 4,
} as const;

export interface InvestigationSaleProps {
  id: number;
  uuid: string;
  sale_id: number;
  contest_product_id: number;
  product_classification_id: number;
  investigation_price_per_share: string | number;
  investigation_price_total: string | number;
  receive_by_cashier: boolean;
  non_winning_ticket?: boolean;
  author_id: number | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  sale?: any;
  author: any | null;
  contest_product_classification?: {
    id: number;
    contest_id: number;
    lottery_id: number;
    product_name: string;
    product_classification_id: number;
    tenant_id: number;
    created_at: string;
    updated_at: string;
    contest: {
      id: number;
      name: string;
    };
    lottery: {
      id: number;
      name: string;
      color: string;
    };
  };
  product_classification?: {
    id: number;
    uuid: string;
    lottery_id: number;
    name: string;
    game_quantity: number;
    dez_quantity: string;
    quota_quantity: number;
    total_without_tariff: number;
    total_with_tariff: number;
    game_percentage: number;
    tariff: number;
    sale_price: number;
    price_without_tariff: number;
    commission_by_quote: number;
    image: string | null;
    is_active: number;
    product_type_id: number;
    tenant_id: number;
    created_at: string;
    updated_at: string;
    deleted_at: string | null;
  };
}

export const PageList = () => {
  const [listData, setListData] = useState<ApiDataProps<InvestigationSalesGrouped>>({
    current_page: 1,
    last_page: 1,
    next_page_url: null,
    data: [],
  });
  const [state, setState] = useState("loading");
  const [selectedStatus, setSelectedStatus] = useState<number>(CONTEST_STATUS.CREATED);

  const { hasPermission } = usePermissions();

  useEffect(() => {
    (async () => {
      try {
        setState("loading");
        const { data } = await getInvestigationSales(1, { contest_status_id: selectedStatus });
        const paginatedData = data?.response;
        setListData({
          current_page: paginatedData?.current_page || 1,
          last_page: paginatedData?.last_page || 1,
          next_page_url: paginatedData?.next_page_url || null,
          data: paginatedData?.data || [],
        });
        setState("production");
      } catch (error) {
        console.error(error);
        setState("production");
      }
    })();
  }, [selectedStatus]);

  const pageActions = {
    next: async () => {
      if (listData?.current_page < listData?.last_page) {
        setState("loading");
        const { data } = await getInvestigationSales(
          listData?.current_page + 1,
          { contest_status_id: selectedStatus }
        );
        const paginatedData = data?.response;
        setListData({
          current_page: paginatedData?.current_page || listData?.current_page + 1,
          last_page: paginatedData?.last_page || listData?.last_page,
          next_page_url: paginatedData?.next_page_url || null,
          data: paginatedData?.data || [],
        });
        setState("production");
      }
    },
    refresh: async () => {
      setState("loading");
      const { data } = await getInvestigationSales(listData?.current_page || 1, { contest_status_id: selectedStatus });
      const paginatedData = data?.response;
      setListData({
        current_page: paginatedData?.current_page || 1,
        last_page: paginatedData?.last_page || 1,
        next_page_url: paginatedData?.next_page_url || null,
        data: paginatedData?.data || [],
      });
      setState("production");
    },
    silentRefresh: async () => {
      const { data } = await getInvestigationSales(listData?.current_page || 1, { contest_status_id: selectedStatus });
      const paginatedData = data?.response;
      setListData({
        current_page: paginatedData?.current_page || 1,
        last_page: paginatedData?.last_page || 1,
        next_page_url: paginatedData?.next_page_url || null,
        data: paginatedData?.data || [],
      });
    },
    previous: async () => {
      if (listData?.current_page > 1) {
        setState("loading");
        const { data } = await getInvestigationSales(
          listData?.current_page - 1,
          { contest_status_id: selectedStatus }
        );
        const paginatedData = data?.response;
        setListData({
          current_page: paginatedData?.current_page || listData?.current_page - 1,
          last_page: paginatedData?.last_page || listData?.last_page,
          next_page_url: paginatedData?.next_page_url || null,
          data: paginatedData?.data || [],
        });
        setState("production");
      }
    },
    lastPage: async () => {
      if (listData?.current_page !== listData?.last_page) {
        setState("loading");
        const { data } = await getInvestigationSales(listData?.last_page, { contest_status_id: selectedStatus });
        const paginatedData = data?.response;
        setListData({
          current_page: paginatedData?.current_page || listData?.last_page,
          last_page: paginatedData?.last_page || listData?.last_page,
          next_page_url: paginatedData?.next_page_url || null,
          data: paginatedData?.data || [],
        });
        setState("production");
      }
    },
  };

  return (
    <Main>
      <If condition={hasPermission("view-sales-calculation-list")}>
        <div className="flex justify-center md:justify-between items-center flex-wrap gap-2">
          <h2 className="text-2xl font-semibold flex gap-2 items-center">
            Apuração de Vendas
          </h2>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">Status do Concurso:</span>
            <SelectComponent
              label=""
              options={[
                { value: String(CONTEST_STATUS.CREATED), label: "Criado" },
                { value: String(CONTEST_STATUS.EXPIRED), label: "Expirado" },
                { value: String(CONTEST_STATUS.FINISHED), label: "Finalizado" },
                { value: String(CONTEST_STATUS.CANCELED), label: "Cancelado" },
              ]}
              onChange={(value) => setSelectedStatus(Number(value))}
              placeholder="Selecione o status"
              value={String(selectedStatus)}
            />
          </div>
        </div>
        <div className="flex flex-col gap-3">
          {state === "loading" ? (
            <CreateLoaderTable cols={5} lines={6} />
          ) : (
            <TableData
              dataApi={listData}
              pageActions={pageActions}
              selectedStatus={selectedStatus}
            />
          )}
        </div>
      </If>
    </Main>
  );
};
